import React from "react"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { TPerformer, TFood, TBeverage } from "../../types"
import { truncate, capitalize } from "../../utils"
import IconFood from "../../graphics/icon-food.svg"
import { TColor } from "../../types"
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { backgroundImageContainer, backgroundImage, text } from "./StandardCard.css"
import { colorArrayToString } from "../../utils"
import * as styles from "./EntityList.css"
import { Link } from "../ui"
import {
  Text,
  Button,
} from "../ui"

export type EntityListProps = {
  title: string
  body?: string
  backgroundColor: TColor
  background?: {
    id: string
    title: string
    gatsbyImageData: any
  }
  buttonText?: string
  desktopRight: boolean
  link?: string
  source: string
  showSummary: boolean
  selectArtists: TPerformer[]
  selectMusic: TPerformer[]
  selectFood: TFood[]
  selectBeverage: TBeverage[]
  selectWine: TBeverage[]
  selectBeer: TBeverage[]
  selectCocktail: TBeverage[]
  displayFullWidth?: boolean
}

export default function EntityList(props: EntityListProps) {

  const allOf = useStaticQuery(
    graphql`
      query {
        allMusic(sort: {position: ASC}) {
          nodes {
            ...PerformerContent
          }
        }
        allArtist(sort: {position: ASC}) {
          nodes {
            ...PerformerContent
          }
        }
        allFood(sort: {position: ASC}) {
          nodes {
            ...FoodContent
          }
        }
        allBeverage(sort: {position: ASC}) {
          nodes {
            ...BeverageContent
          }
        }
        allBeverageWine: allBeverage(
          sort: {position: ASC}
          filter: {cuisines: {elemMatch: {slug: {eq: "wine"}}}}
          ) {
          nodes {
            ...BeverageContent
          }
        }
        allBeverageBeer: allBeverage(
          sort: {position: ASC}
          filter: {cuisines: {elemMatch: {slug: {eq: "beer"}}}}
          ) {
          nodes {
            ...BeverageContent
          }
        }
        allBeverageCocktail: allBeverage(
          sort: {position: ASC}
          filter: {cuisines: {elemMatch: {slug: {eq: "cocktail"}}}}
          ) {
          nodes {
            ...BeverageContent
          }
        }
      }
    `
  )
  const allArtists = allOf.allArtist.nodes
  const allMusic = allOf.allMusic.nodes
  const allFood = allOf.allFood.nodes
  const allBeverage = allOf.allBeverage.nodes
  const allWine = allOf.allBeverageWine.nodes
  const allBeer = allOf.allBeverageBeer.nodes
  const allCocktail = allOf.allBeverageCocktail.nodes
  

  let slug = 'music'
  const source = (source: string) => {
    switch(source) {
      case 'all_artists':
        slug = 'artists'
        return allArtists
      case 'all_music':
        return allMusic
      case 'all_food':
        slug = 'culinary'
        return allFood
      case 'all_beverage':
        slug = 'beverage'
        return allBeverage
      case 'all_wine':
        slug = 'beverage'
        return allWine
      case 'all_beer':
        slug = 'beverage'
        return allBeer
      case 'all_cocktail':
        slug = 'beverage'
        return allCocktail
      case 'select_artists':
        slug = 'artists'
        return props.selectArtists
      case 'select_music':
        return props.selectMusic
      case 'select_food':
        slug = 'culinary'
        return props.selectFood
      case 'select_beverage':
        slug = 'beverage'
        return props.selectBeverage      
      default:
        slug = null
        return null
    }
  }

  const list = source(props.source)

  const size = {height:"24px", width:"24px"};

  return list ? (
    <div className={styles.entityListContainer} data-card={props.backgroundColor || props.background ? true : false } style={{ backgroundColor: props.backgroundColor ? colorArrayToString(props.backgroundColor) : "transparent" }}>
      <div className={styles.content}>
        <div className={styles.contentText}>
          {props.title && (
            <div>
              {props.background || props.backgroundColor ? (
                <Text as="h2" variant="superHeading"><span style={{ lineHeight: 1, display: "block" }}>{props.title}</span></Text>
              ) : (
                <h2 className={styles.entityTitle}>{props.title}</h2>
              )}
            </div>
          )}
          {props.body && (
            <div className={text}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{props.body}</ReactMarkdown>
            </div>
          )}
        </div>
        <div className={styles.entities} style={{ marginBottom: "60px" }}>
          {list.map((entity) => {
            const link = `/${slug}/${entity.slug}`
            let features = null
            if(entity.features || entity.cuisine?.length > 0) {
              const unformattedFeatures = entity.features ? eval(entity.features.replace('\\n  \\', '')) as [] : []
              const attributes = unformattedFeatures.map(feature => {
                return capitalize(feature)
              })
              const cuisines = entity.cuisines?.map(cuisine => {
                return cuisine.title
              })
              features = cuisines.concat(attributes).join(', ')
            }

            return !props.showSummary ? (
              <div key={entity.id} className={styles.itemContainer}>
                <Link className={styles.item} to={link}>
                  <div className={styles.imageContainer}>
                    {entity.avatar && (
                      <GatsbyImage
                        alt={entity.title || "item image"}
                        className={styles.image}
                        image={getImage(entity.avatar)}
                      />
                    )}
                    <h3 className={styles.entityTitle} style={{ margin: 0 }}>{entity.title || null}</h3>
                  </div>
                </Link>
              </div>
            ) : (
              <div key={entity.id} className={styles.summaryEntity}>
                <Link className={styles.summaryLink} to={link}>
                  <div className={styles.summaryImageContainerOuter}>
                    <div className={styles.summaryImageContainer}>
                      <div className={styles.summaryImageContainerInner}>
                        {entity.avatar && (
                          <GatsbyImage
                            alt={entity.title || "item image"}
                            className={styles.summaryImage}
                            image={getImage(entity.avatar)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.summaryContent}>
                    <h3 className={styles.entityTitle} style={{ margin: 0 }}>{entity.title || null}</h3>
                    {features && (
                      <div className={styles.contributorSummaryMeta}>
                        <IconFood style={{size}} /> {features}
                      </div>
                    )}
                    <div>{truncate(entity.summary, 150)}</div>
                  </div>
                </Link>
              </div>
            )
          })}
        </div>
        {props.link && props.buttonText && (
          <div className={styles.button} >
            <Button to={props.link}
              variant="entityListMore"
            >{props.buttonText}</Button>
          </div>
        )}
      </div>
      {props.background && (
        <div className={backgroundImageContainer}>
          {props.background && (
          <GatsbyImage
            className={backgroundImage}
            alt={props.background.title || "Entity List Background"}
            image={getImage(props.background)}
            />
          )}
        </div>
      )}
    </div>
  ) : <></>
}

export const query = graphql`
  fragment EntityListContent on EntityList {
    id
    title
    body
    buttonText
    displayFullWidth
    link
    background {
      id
      title
      ... on DatoCmsAsset {
        gatsbyImageData(
          imgixParams: {crop: "focalpoint", fit: "crop", w: "1200", h: "1200"}
        )
      }
    }
    backgroundColor {
      red
      blue
      green
      alpha
    }
    source
    showSummary
    desktopRight
    selectArtists {
      ...PerformerContent
    }
    selectMusic {
      ...PerformerContent
    }
    selectFood {
      ...FoodContent
    }
    selectBeverage {
      ...BeverageContent
    }
  }
`